import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { BoundsRect, IMapInstance, IMapMarker, LatLng, MarkerOptions, RouteLineMode } from "../map-provider.interface";
import { failIfRouteUnansweredAsync } from "../route-timeout";
import { GoogleMapsLoader, IGoogleMapsAuth } from "./google-maps-loader";
import { GoogleMarker } from "./google-marker";
import { Loader } from "@googlemaps/js-api-loader";

export class GoogleMapInstance implements IMapInstance {
    private readonly _map: google.maps.Map;
    private readonly _loader: Loader;
    private readonly _auth: IGoogleMapsAuth;

    private readonly _directionsService = new google.maps.DirectionsService();
    private readonly _infoWindow = new google.maps.InfoWindow();

    private _boundsListener: google.maps.MapsEventListener | null = null;
    private _idleListener: google.maps.MapsEventListener | null = null;
    private _directionsRenderer?: google.maps.DirectionsRenderer;
    private _routePolyline?: google.maps.Polyline;

    private _markers: GoogleMarker[] = [];
    private _clusterer: MarkerClusterer | null = null;
    private readonly _infoWindowObserver: MutationObserver;

    private static _infoWindowTitleId = 0;

    constructor(map: google.maps.Map, loader: Loader, auth: IGoogleMapsAuth = GoogleMapsLoader) {
        this._map = map;
        this._loader = loader;
        this._auth = auth;

        // Google's close button carries both a title and an aria-label, which reads as a
        // duplicate. Re-runs on every open because Google rebuilds the button each time.
        this._infoWindow.addListener("domready", () => this._sanitizeInfoWindowCloseButton());

        // Google focuses the close button before attaching the dialog, so screen readers announce
        // it twice. Handle only the attach mutation; reacting to all would steal focus back on Tab.
        this._infoWindowObserver = new MutationObserver(records => this._onMapDomMutated(records));
        this._infoWindowObserver.observe(this._map.getDiv(), { childList: true, subtree: true });
    }

    // ---------------------------------------------
    // Marker APIs
    // ---------------------------------------------
    async addMarkerAsync(coords: LatLng, options?: MarkerOptions): Promise<IMapMarker> {
        const { AdvancedMarkerElement } = await this._loader.importLibrary("marker");

        const content = this._getMarkerContent(options?.iconUrl ?? "");

        // Google swaps the map for its error page when it rejects the key, and a marker attached to
        // or detached from it after that throws.
        const marker = new AdvancedMarkerElement({
            map: this._auth.isKeyRejected() ? null : this._map,
            position: coords,
            content
        });

        const handle = new GoogleMarker(marker);
        this._markers.push(handle);

        // ! Commented because of performance issues. Bug(hotfix) #74791: it updates for every marker added,
        // ! this is already handled by setClusterMarkers() in the caller(locations.ts), which is used after all markers are added in bulk.
        // ! Delete these comments once it's revised by dev lead.

        // If clustering is enabled, refresh clusterer markers
        // if (this._clusterer) {
        //     this._clusterer.clearMarkers();
        //     this._clusterer.addMarkers(this._markers.map(m => m._native()));
        // }

        if (options?.center) {
            this._map.setCenter(coords);
            if (typeof options.zoom === "number") this._map.setZoom(options.zoom);
        }

        return handle;
    }

    removeMarker(marker: IMapMarker): void {
        if (!marker) return;

        this._markers = this._markers.filter(m => m !== marker);

        // A rejected key already took the marker down with the map.
        if (this._auth.isKeyRejected()) return;

        // Remove from map via your wrapper
        if (typeof marker.remove === "function") {
            marker.remove();
        } else {
            // Fallback: remove native marker from map
            const native = (marker as any)._native?.();
            if (native?.map !== undefined)
                native.map = null; // AdvancedMarkerElement
            else if (typeof native?.setMap === "function")
                native.setMap(null); // legacy Marker
        }

        // Refresh clusterer markers (if enabled)
        if (this._clusterer) {
            this._clusterer.clearMarkers();
            this._clusterer.addMarkers(this._markers.map(m => m._native()));
        }

        // Optional: don't close info here unless you want removing any marker to close it.
        // this.closeInfo();
    }

    clearMarkers(): void {
        // A rejected key already took the markers down with the map.
        if (!this._auth.isKeyRejected()) {
            for (const m of this._markers)
                m.remove();

            this._clusterer?.clearMarkers();
        }

        this._markers = [];
        this.closeInfo();
    }

    // ---------------------------------------------
    // Routing / Overlays APIs
    // ---------------------------------------------
    async drawRouteAsync(origin: LatLng, destination: LatLng, mode?: RouteLineMode): Promise<void> {
        // A rejected key leaves Google's error page where the map was, so no route can be shown.
        if (this._auth.isKeyRejected())
            throw new Error("Google rejected the API key.");

        this.clearRoute();

        if (mode === "straight")
            this._drawStraightRoute(origin, destination);
        else if (mode === "route")
            await this._drawTrueRouteAsync(origin, destination);
    }

    clearRoute(): void {
        // A rejected key already took the route down with the map.
        if (!this._auth.isKeyRejected()) {
            this._directionsRenderer?.setDirections(null);
            this._routePolyline?.setMap(null);
        }

        this._routePolyline = undefined;
    }

    // ---------------------------------------------
    // Info window APIs
    // ---------------------------------------------
    openInfo(anchor: IMapMarker, html: string | HTMLElement): void {
        const native = (anchor as GoogleMarker)._native();

        this._infoWindow.close();
        this._infoWindow.setContent(html);
        this._infoWindow.open({
            map: this._map,
            anchor: native
        });
    }

    closeInfo(): void {
        this._infoWindow.close();
    }

    // ---------------------------------------------
    // Viewport / Camera APIs
    // ---------------------------------------------
    setCenter(coords: LatLng): void {
        this._map.setCenter(coords);
    }

    getZoom(): number {
        return this._map.getZoom() ?? 0;
    }

    setZoom(zoom: number): void {
        this._map.setZoom(zoom);
    }

    isInView(coords: LatLng): boolean {
        const bounds = this._map.getBounds();
        if (!bounds)
            return false;
        return bounds.contains(coords as google.maps.LatLngLiteral);
    }

    resize(): void {
        // Common SPA fix when container becomes visible.
        // Trigger resize and re-apply center.
        //const center = this._map.getCenter();
        //google.maps.event.trigger(this._map, "resize");
        //if (center) this._map.setCenter(center);
    }

    // ---------------------------------------------
    // Clustering APIs
    // ---------------------------------------------
    setClusterMarkers(markers: IMapMarker[]): void {
        // A rejected key already took the map down, so there is nothing to cluster on.
        if (this._auth.isKeyRejected()) return;

        const nativeMarkers = markers.map(m => (m as GoogleMarker)._native());

        if (this._clusterer) {
            this._clusterer.clearMarkers();
            this._clusterer.addMarkers(nativeMarkers);
        } else {
            this._clusterer = new MarkerClusterer({ map: this._map, markers: nativeMarkers });
        }
    }

    // ---------------------------------------------
    // Bounds APIs
    // ---------------------------------------------
    getBoundsRect(): BoundsRect | null {
        const b = this._map.getBounds();
        if (!b) return null;

        const ne = b.getNorthEast();
        const sw = b.getSouthWest();

        return {
            north: ne.lat(),
            east: ne.lng(),
            south: sw.lat(),
            west: sw.lng()
        };
    }

    fitBoundsRect(bounds: BoundsRect): void {
        const b = new google.maps.LatLngBounds(
            { lat: bounds.south, lng: bounds.west },
            { lat: bounds.north, lng: bounds.east }
        );

        this._map.fitBounds(b);
    }

    // ---------------------------------------------
    // Events
    // ---------------------------------------------
    onIdle(handler: () => void): void {
        if (this._idleListener)
            this._idleListener.remove();

        this._idleListener = this._map.addListener("idle", handler);
    }

    onBoundsChanged(handler: () => void): () => void {
        // remove prior handler (single active subscription per instance)
        if (this._boundsListener) {
            this._boundsListener.remove();
            this._boundsListener = null;
        }

        const listener = this._map.addListener("bounds_changed", handler);
        this._boundsListener = listener;

        // return unsubscribe
        return () => {
            // only remove if it's still the active one
            if (this._boundsListener === listener) {
                this._boundsListener.remove();
                this._boundsListener = null;
            } else {
                // fallback: still remove the listener we created
                listener.remove();
            }
        };
    }

    // ---------------------------------------------
    // Helpers
    // ---------------------------------------------
    /** Handles the dialog being attached, ignoring Google's constant tile/marker churn. */
    private _onMapDomMutated(records: MutationRecord[]): void {
        for (const record of records) {
            record.addedNodes.forEach(node => {
                if (!(node instanceof HTMLElement)) return;

                const dialog = node.classList.contains("gm-style-iw")
                    ? node
                    : node.querySelector<HTMLElement>(".gm-style-iw");

                if (dialog)
                    this._prepareInfoWindowDialog(dialog);
            });
        }
    }

    /** Names the dialog and takes focus off the close button, so it is announced once. */
    private _prepareInfoWindowDialog(dialog: HTMLElement): void {
        // Google's aria-labelledby target is its own empty header, leaving the dialog unnamed.
        const heading = dialog.querySelector<HTMLElement>("h2");
        if (heading) {
            if (!heading.id)
                heading.id = `gm_info_window_title_${++GoogleMapInstance._infoWindowTitleId}`;

            if (dialog.getAttribute("aria-labelledby") !== heading.id)
                dialog.setAttribute("aria-labelledby", heading.id);
        }

        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement?.classList?.contains("gm-ui-hover-effect")) {
            dialog.tabIndex = -1;
            dialog.focus();
        }
    }

    private _sanitizeInfoWindowCloseButton(): void {
        const closeButton = this._map.getDiv()?.querySelector<HTMLElement>(".gm-ui-hover-effect");
        if (!closeButton) return;

        closeButton.removeAttribute("title");
        closeButton.setAttribute("aria-label", "Close");

        closeButton.querySelectorAll<HTMLElement>("*").forEach(child => {
            child.setAttribute("aria-hidden", "true");
            child.removeAttribute("role");
            child.removeAttribute("tabindex");
        });
    }

    private _getMarkerContent(url: string): HTMLElement {
        const img = document.createElement("img");
        img.src = url;
        img.style.width = "30px";
        img.style.height = "30px";
        img.alt = "location icon";
        return img;
    }

    private _drawStraightRoute(origin: LatLng, destination: LatLng): void {
        // Draw a simple polyline between points
        const path = [
            { lat: origin.lat, lng: origin.lng },
            { lat: destination.lat, lng: destination.lng }
        ];

        // Keep a reference so clearRoute can remove it
        this._routePolyline = new google.maps.Polyline({
            path,
            map: this._map
            // don’t specify colors unless you want to; Google defaults are fine
        });
    }

    private async _drawTrueRouteAsync(origin: LatLng, destination: LatLng): Promise<void> {
        // Lazily create renderer once
        this._directionsRenderer ??= new google.maps.DirectionsRenderer({
            map: this._map,
            suppressMarkers: true,      // you already place your own markers
            preserveViewport: true      // don't fight your fitBounds logic unless you want it to
        });

        const request: google.maps.DirectionsRequest = {
            origin: { lat: origin.lat, lng: origin.lng },
            destination: { lat: destination.lat, lng: destination.lng },
            travelMode: google.maps.TravelMode.DRIVING
        };

        const response = await this._requestDirectionsAsync(request);
        this._directionsRenderer.setDirections(response);
    }

    /**
     * Google never answers a request once it rejects the key, so the route fails as soon as it does.
     * The key is watched inside the timeout so a request that times out stops watching it too.
     */
    private async _requestDirectionsAsync(
        request: google.maps.DirectionsRequest
    ): Promise<google.maps.DirectionsResult> {
        let stopWatchingAuth: () => void = () => undefined;

        const keyRejected = new Promise<never>((_, reject) => {
            stopWatchingAuth = this._auth.onAuthFailure(
                () => reject(new Error("Google rejected the API key."))
            );
        });

        try {
            return await failIfRouteUnansweredAsync(
                Promise.race([this._directionsService.route(request), keyRejected])
            );
        }
        finally {
            stopWatchingAuth();
        }
    }
}